/**
 * offlineQueue — the FACADE over the offline write queue.
 *
 * Generic queue for mutations that failed or were made with no connectivity. Stored
 * in IndexedDB; flushed on the `online` event, on a 30s interval, or manually. Each
 * entry has a type + payload; consumers register a handler per type. Max 5 retries,
 * then the entry is marked failed and SURFACED (never dropped).
 *
 * The engine was one 297-LOC file against a 300-LOC hard gate, so curing
 * BUG-2026-07-14-VC559 required splitting it. Decomposed by COHESION, strictly
 * one-way so `check:circular` (madge) stays green:
 *
 *   queueStore    — what is IN the queue (listeners, counts, reads)
 *   queueStoreWrite — the ONE serialised door every queue write goes through
 *   queueRecovery — how a STRANDED entry re-enters the flush lane, with no human tap
 *   queueFlush    — how it DRAINS (probe-gated, honest skip reasons)
 *   queueExit     — how a rep gets an entry OUT (retry / discard)
 *   offlineQueue (this file) — enqueue + the stable import surface
 *
 * This file owns `enqueue` and re-exports the rest, so every existing
 * `from '@appic/lib/offline/offlineQueue'` import keeps resolving unchanged.
 */

import { captureFailedWriteRetained } from './failedWriteCapture'
import { getReachability } from './reachability'
import { isCritical, MAX_RETRIES_DEFAULT } from './queueStore'
import { updateQueue } from './queueStoreWrite'
import type { QueueEntry } from './offlineQueueTypes'

// Re-export the queue's data contracts + the store/flush/exit surface.
export { ConflictError, QUEUE_FAILURE_REASONS } from './offlineQueueTypes'
export type {
  QueueEntry,
  QueueHandler,
  ConflictEvent,
  FlushResult,
  FlushSkipReason,
  RetryFailedResult,
  QueueEntryView,
  QueueFailureReason,
  DiscardOutcome,
} from './offlineQueueTypes'
export {
  registerQueueHandler,
  onQueueChange,
  onConflict,
  onFlushResult,
  getPendingCount,
  getQueueEntries,
  getFailedCount,
  getFailedEntryIds,
  isFinalPinned,
} from './queueStore'
export { removeQueueEntry, pinQueueEntryFinal, removeDeliveredTwins } from './queueStoreWrite'
export { flushQueue, startOfflineQueue, stopOfflineQueue, recoverFailedEntriesOnce } from './queueFlush'
export type { QueueRecoveryResult } from './queueRecovery'
export {
  retryFailedEntries,
  discardEntry,
  describeQueueEntries,
  isRetryableReason,
} from './queueExit'

/**
 * Add an entry to the offline queue.
 *
 * DURABLE-AT-ENQUEUE (operator 2026-07-12, FR-FE-DURABLE-CAPTURE-AT-ENQUEUE):
 * "QUEUED IS NOT SAVED." The IndexedDB queue lives on ONE rep's ONE device — a
 * lost/wiped/reinstalled phone takes the report with it, and the write path was dead
 * for days while every submit reported success off a queue entry nobody could
 * recover. So the durable off-device copy is written the MOMENT a critical write is
 * queued, not five failed replays later (a point that never arrives if the device
 * goes offline for good).
 *
 * The off-device capture routes through `captureFailedWriteRetained` (the confirmed,
 * loud-on-non-confirm variant): the IDB entry above is the durable copy, and the entry
 * is retained even if that capture cannot confirm — a sink failure never costs the
 * payload (T8, FR-FE-IRONCLAD-TELEMETRY-OUTBOX). The first-replay-failure capture
 * (replayFailure.ts) remains the online catch-all.
 *
 * @returns the minted entry `id`, so a caller doing persist-before-network
 *   (useOfflineMutation) can remove exactly this entry once the server confirms.
 */
export async function enqueue(
  type: string,
  payload: unknown,
  maxRetries = MAX_RETRIES_DEFAULT,
  opts: {
    /**
     * Fire the off-device durable capture at enqueue time. Default `true`
     * (FR-FE-DURABLE-CAPTURE-AT-ENQUEUE: a critical write parked on a device gets an
     * off-device copy immediately). `useOfflineMutation` passes `false` for its
     * persist-before-network write: that enqueue is SPECULATIVE (it precedes the
     * network attempt and is removed on success), so paging an alertable Sentry issue
     * for it would fire on EVERY submit. On that path the immediate off-device copy is
     * unnecessary — the entry is the durable copy, and the replay path captures
     * off-device on the first real failure.
     */
    captureOffDevice?: boolean
  } = {},
): Promise<string> {
  const captureOffDevice = opts.captureOffDevice ?? true
  const entry: QueueEntry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type, payload, retries: 0, maxRetries,
    createdAt: new Date().toISOString(), status: 'pending',
  }
  // Appended to the CURRENT array inside the serialised chain — a flush in flight, or a
  // second enqueue in the same tick, can no longer write a stale array over this one.
  await updateQueue(current => ({ next: [...current, entry], result: undefined }))

  if (captureOffDevice && isCritical(type)) {
    // Awaited so a non-confirm is handled HERE (retain), deterministically — the entry
    // is already durably in IDB above, so a thrown/failed sink leaves it in place.
    await captureFailedWriteRetained(type, payload, 'queued-not-saved', {
      id: entry.id,
      retries: entry.retries,
      createdAt: entry.createdAt,
      // The forensic field that mattered and did not exist: was the server ACTUALLY
      // reachable when this report got parked on the device? The meta used to record
      // `navigator.onLine` — i.e. the very flag that lied — so the capture agreed with
      // the bug and nobody could tell a real outage from a phantom one. This records
      // the PROBE's verdict ('reachable' here means the write was queued despite a
      // working network, which is an ALARM, not a normal offline enqueue).
      reachability: getReachability(),
    })
  }

  return entry.id
}

/** Remove entries matching a type + a predicate on the payload. */
export async function dequeue(type: string, match: (payload: unknown) => boolean): Promise<void> {
  await updateQueue(current => ({
    next: current.filter(e => !(e.type === type && match(e.payload))),
    result: undefined,
  }))
}
